// NPM packages
import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import styled from '@emotion/styled';
import { DateTime } from 'luxon';

// All other imports
import PageContent from 'components/core/PageContent';
import Link from 'components/core/Link';
import H1 from 'components/core/H1';

const Root = styled(PageContent)({});

const StyledTable = styled(Table)(({ theme: { palette, typography } }) => ({
  '& tr:nth-of-type(even)': {
    backgroundColor: palette.grey[100],
  },
  '& thead tr': {
    backgroundColor: palette.grey[300],
  },
  '& thead th': {
    fontWeight: typography.fontWeightBold,
    fontSize: '1rem',
  },
}));
const DateCell = styled(TableCell)({
  whiteSpace: 'nowrap',
});
const TypeCell = styled(TableCell)({
  width: '15rem',
});

enum EventType {
  STAKEHOLDER_MEETING = 'Stakeholder Group Meeting',
  OPEN_SCIENCE_WEBINAR = 'Open Science and FAIR Data Working Group Webinar',
  OUTREACH_MEETING = 'Outreach Presentation',
  LIVE_DEMONSTRATION = 'Live Demonstration',
  TRAINING_WORKSHOP = 'Training Workshop',
}

const events = [
  {
    type: EventType.STAKEHOLDER_MEETING,
    date: DateTime.local(2023, 8, 10),
    description: 'Quarterly meeting of the MSD-LIVE Stakeholder Group',
  },
  {
    type: EventType.STAKEHOLDER_MEETING,
    date: DateTime.local(2023, 5, 11),
    description: 'Quarterly meeting of the MSD-LIVE Stakeholder Group',
  },
  {
    type: EventType.STAKEHOLDER_MEETING,
    date: DateTime.local(2023, 2, 9),
    description: 'Quarterly meeting of the MSD-LIVE Stakeholder Group',
  },
  {
    type: EventType.STAKEHOLDER_MEETING,
    date: DateTime.local(2022, 11, 10),
    description: 'Quarterly meeting of the MSD-LIVE Stakeholder Group',
  },
  {
    type: EventType.LIVE_DEMONSTRATION,
    date: DateTime.local(2022, 11, 1),
    description:
      'Demonstration of the MSD-LIVE data repository to the GCIMS project team',
  },
  {
    type: EventType.OUTREACH_MEETING,
    date: DateTime.local(2022, 8, 23),
    description: (
      <>
        Community-wide webinar introducing the capabilities in the MSD-LIVE v1.0
        release:{' '}
        <Link to="https://gcc02.safelinks.protection.outlook.com/?url=https%3A%2F%2Fcornell.zoom.us%2Fmeeting%2Fregister%2FtJIlcOGsrz8rHtKwQD7nZXJFEjlLXHtURBMt&data=05%7C01%7Cdevin.mcallester%40pnnl.gov%7Cc041657a28254c6639c308da7c7e866e%7Cd6faa5f90ae240338c0130048a38deeb%7C0%7C0%7C637959180105428769%7CUnknown%7CTWFpbGZsb3d8eyJWIjoiMC4wLjAwMDAiLCJQIjoiV2luMzIiLCJBTiI6Ik1haWwiLCJXVCI6Mn0%3D%7C3000%7C%7C%7C&sdata=4SSfkllfWLGJUQexV9Xy5v6vGtphg7iDGo4ZAZj6tEc%3D&reserved=0">
          Register here
        </Link>
      </>
    ),
  },
  {
    type: 'v1.0 Release of MSD-LIVE',
    date: DateTime.local(2022, 8, 22),
    description: 'MSD-LIVE data repository launches to the MSD community',
  },
  {
    type: EventType.TRAINING_WORKSHOP,
    date: DateTime.local(2022, 8, 3),
    description: 'Docker workshop to the GCIMS project team',
  },
  {
    type: EventType.TRAINING_WORKSHOP,
    date: DateTime.local(2022, 7, 29),
    description:
      'Metarepository and GitHub workshop to the ICoM and COMPASS-GLM project teams',
  },
  {
    type: EventType.TRAINING_WORKSHOP,
    date: DateTime.local(2022, 8, 3),
    description: 'Docker workshop to the GCIMS project team',
  },
  {
    type: EventType.TRAINING_WORKSHOP,
    date: DateTime.local(2022, 7, 29),
    description:
      'Metarepository and GitHub workshop to the ICoM and COMPASS-GLM project teams',
  },
  {
    type: 'Outreach Presentation',
    date: DateTime.local(2022, 6, 7),
    description: 'Overview of MSD-LIVE at the GCAM annual meeting',
  },
  {
    type: EventType.TRAINING_WORKSHOP,
    date: DateTime.local(2022, 5, 25),
    description:
      'Data and code management best practices workshop to the ICoM and COMPASS-GLM project teams',
  },
  {
    type: EventType.LIVE_DEMONSTRATION,
    date: DateTime.local(2022, 5, 18),
    description:
      'JupyterHub support for a mosartwmpy clinic at the CSDMS annual meeting',
  },
  {
    type: EventType.STAKEHOLDER_MEETING,
    date: DateTime.local(2022, 5, 12),
    description: 'Quarterly meeting of the MSD-LIVE Stakeholder Group​',
  },
  {
    type: EventType.OUTREACH_MEETING,
    date: DateTime.local(2022, 3, 29),
    description: 'Briefing on MSD-LIVE to the InteRFACE project team',
  },
  {
    type: EventType.OUTREACH_MEETING,
    date: DateTime.local(2022, 3, 15),
    description: 'Briefing on MSD-LIVE to the MSD PI group',
  },
  {
    type: EventType.STAKEHOLDER_MEETING,
    date: DateTime.local(2022, 2, 24),
    description: 'Quarterly meeting of the MSD-LIVE Stakeholder Group​',
  },
  {
    type: EventType.STAKEHOLDER_MEETING,
    date: DateTime.local(2021, 11, 11),
    description: 'Quarterly meeting of the MSD-LIVE Stakeholder Group​',
  },
  {
    type: EventType.OPEN_SCIENCE_WEBINAR,
    date: DateTime.local(2021, 11, 9),
    description:
      'From Storylines to Anthologies: Systematically examining the implications of climate change on known weather extremes and their multi-sectoral impacts​',
  },
  {
    type: EventType.OPEN_SCIENCE_WEBINAR,
    date: DateTime.local(2021, 10, 12),
    description:
      'Overview of the Geospatial Analytics for Multisectoral Urban Teleconnections (gamut) Package​',
  },
  {
    type: EventType.STAKEHOLDER_MEETING,
    date: DateTime.local(2021, 8, 12),
    description: 'Quarterly meeting of the MSD-LIVE Stakeholder Group​',
  },
  {
    type: EventType.LIVE_DEMONSTRATION,
    date: DateTime.local(2021, 5, 18),
    description:
      'JupyterHub support for a GCAM-Demeter clinic at the CSDMS annual meeting',
  },
  {
    type: EventType.STAKEHOLDER_MEETING,
    date: DateTime.local(2021, 5, 11),
    description: 'Quarterly meeting of the MSD-LIVE Stakeholder Group​',
  },
  {
    type: EventType.OPEN_SCIENCE_WEBINAR,
    date: DateTime.local(2021, 5, 4),
    description:
      'Assessing Compounding and Co-Evolving Risks Across Multiple Systems and Sectors: A Triage-based Approach',
  },
];

export interface NewsAndEventsProps {
  className?: string;
}

export default function NewsAndEvents(
  props: NewsAndEventsProps
): React.ReactElement | null {
  const { className } = props;
  return (
    <Root className={className}>
      <H1>News and Events</H1>
      <TableContainer component={Paper}>
        <StyledTable>
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Description</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {events.map((event, index) => (
              <TableRow
                // index is OK since events never change
                key={index}
              >
                <TypeCell>{event.type}</TypeCell>
                <DateCell>
                  {event.date.toLocaleString(DateTime.DATE_MED)}
                </DateCell>
                <TableCell>{event.description}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </StyledTable>
      </TableContainer>
    </Root>
  );
}
